import type { Amount, LedgerRow } from './analysis'

/** Lower case, letters and digits only: "mcdonalds" finds "McDonald's", "pge" finds "P.G.E.". */
export const fold = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '')

/** True when the folded text appears in the row's merchant or statement description. */
export function textMatches(query: string, r: LedgerRow): boolean {
  const q = fold(query)
  return !q || fold(r.merchant).includes(q) || fold(r.description).includes(q)
}

export interface MerchantHit {
  merchant: string
  count: number
  /** Net money out, refunds taken off; negative when the merchant paid you. */
  net: number
  last: string
}

/**
 * Merchants whose name or statement text contains the query, across every
 * transaction on file. Name matches come before description-only matches,
 * then the most frequent.
 */
export function findMerchants(ledger: LedgerRow[], query: string, limit = 8): MerchantHit[] {
  const q = fold(query)
  if (!q) return []
  const hits = new Map<string, MerchantHit & { byName: boolean }>()
  for (const r of ledger) {
    const byName = fold(r.merchant).includes(q)
    if (!byName && !fold(r.description).includes(q)) continue
    const h = hits.get(r.merchant) ?? { merchant: r.merchant, count: 0, net: 0, last: '', byName: false }
    h.count++
    h.net -= r.amount
    if (r.date > h.last) h.last = r.date
    h.byName ||= byName
    hits.set(r.merchant, h)
  }
  return [...hits.values()]
    .sort((a, b) => Number(b.byName) - Number(a.byName) || b.count - a.count || a.merchant.localeCompare(b.merchant))
    .slice(0, limit)
    .map(({ byName: _, ...h }) => ({ ...h, net: round(h.net) }))
}

export interface History {
  count: number
  /** Net money out, refunds taken off; negative when it is money in. */
  net: number
  average: number
  first: string
  last: string
  /** Every month from the first to the last, empty months included, so gaps show. */
  months: Amount[]
  perMonth: number
}

/** What a list of transactions adds up to over time. Null for an empty list. */
export function history(rows: LedgerRow[]): History | null {
  if (rows.length === 0) return null
  const dates = rows.map((r) => r.date).sort()
  const first = dates[0]
  const last = dates[dates.length - 1]
  const byMonth = new Map<string, Amount>()
  for (const m of monthsBetween(first.slice(0, 7), last.slice(0, 7))) byMonth.set(m, { label: m, total: 0, count: 0 })
  let net = 0
  for (const r of rows) {
    const m = byMonth.get(r.month)
    if (m) {
      m.total -= r.amount
      m.count++
    }
    net -= r.amount
  }
  const months = [...byMonth.values()].map((m) => ({ ...m, total: round(m.total) }))
  return { count: rows.length, net: round(net), average: round(net / rows.length), first, last, months, perMonth: round(net / months.length) }
}

/**
 * Net money out per month for everything matching the query, over every month
 * on file, so the axis holds still while the query changes. Null when nothing
 * matches.
 */
export function monthlyTotals(ledger: LedgerRow[], query: string): { months: Amount[]; net: number; count: number } | null {
  if (ledger.length === 0 || !fold(query)) return null
  const all = ledger.map((r) => r.month).sort()
  const byMonth = new Map(monthsBetween(all[0], all[all.length - 1]).map((m) => [m, { label: m, total: 0, count: 0 }]))
  let net = 0
  let count = 0
  for (const r of ledger) {
    if (!textMatches(query, r)) continue
    const m = byMonth.get(r.month)!
    m.total -= r.amount
    m.count++
    net -= r.amount
    count++
  }
  if (count === 0) return null
  return { months: [...byMonth.values()].map((m) => ({ ...m, total: round(m.total) })), net: round(net), count }
}

/** `YYYY-MM` months from `from` to `to`, both included. */
export function monthsBetween(from: string, to: string): string[] {
  const out: string[] = []
  let [y, m] = from.split('-').map(Number)
  for (let guard = 0; guard < 600; guard++) {
    const month = `${y}-${String(m).padStart(2, '0')}`
    if (month > to) break
    out.push(month)
    if (++m > 12) [y, m] = [y + 1, 1]
  }
  return out
}

const round = (n: number) => Math.round(n * 100) / 100
